// WebSocket transport to the stream relay. Speaks the protocol documented in
// stream-relay/src/protocol.js.
//
// - Sequenced binary frames [1B type][4B seq BE][payload]; every media
//   segment is acked by the relay once it flushes into ffmpeg — the ack rate
//   is our uplink bandwidth measurement.
// - Un-acked frames are kept in a ring buffer so a reconnect inside the
//   relay's grace window resumes the SAME publish (retransmit from lastSeq+1,
//   ffmpeg never disconnects). If the buffer overflowed or the session
//   expired, we reconnect fresh and the engine restarts with a new init
//   segment.
// - Reconnects forever (0.5s -> 5s capped backoff) until close() is called,
//   except after the relay closed an idle connection (idle_timeout): then it
//   stays down until connect() is called again.

const FRAME_INIT = 0x01;
const FRAME_MEDIA = 0x02;
const RING_MAX_BYTES = 8 * 1024 * 1024;
const BACKOFF_MS = [300, 800, 1500, 2000]; // capped low; 'online' events retry instantly
const PING_INTERVAL_MS = 5000;
const PONG_DEAD_MS = 15000; // no pong for this long on an OPEN socket = dead
const CONNECT_TIMEOUT_MS = 6000; // stuck in CONNECTING = dead

export function createTransport({ url, streamKey, container = "fmp4", handlers }) {
  let ws = null;
  let seq = 0;
  let sessionId = null;
  let closed = false;
  let suspended = false; // relay closed us for not publishing; wait for connect()
  let attempts = 0;
  let ring = []; // { seq, buf } un-acked frames, oldest first
  let ringBytes = 0;
  let ringBroken = false; // overflowed -> cannot resume seamlessly
  let pingTimer = null;
  let reconnectTimer = null;
  let connectTimer = null;
  let lastPongAt = 0;
  let rtt = 0;

  // Network flaps: OS-level events beat TCP timeouts by ~30-60s. Kill the
  // socket immediately when offline; retry immediately when back online.
  const onOffline = () => {
    try {
      ws?.close();
    } catch {}
  };
  const onOnline = () => {
    if (closed || suspended) return;
    clearTimeout(reconnectTimer);
    try {
      if (ws && ws.readyState !== 1) ws.close();
    } catch {}
    connect();
  };
  if (typeof window !== "undefined") {
    window.addEventListener("offline", onOffline);
    window.addEventListener("online", onOnline);
  }
  let ackWindow = []; // { at, bytes } for uplink kbps
  let sentWindow = []; // { at, bytes }
  const bySeqBytes = new Map();

  const emit = (name, ...args) => handlers[name]?.(...args);

  function frame(type, payload) {
    const buf = new Uint8Array(5 + payload.byteLength);
    buf[0] = type;
    new DataView(buf.buffer).setUint32(1, seq);
    buf.set(payload instanceof Uint8Array ? payload : new Uint8Array(payload), 5);
    const out = { seq, buf };
    seq += 1;
    return out;
  }

  function pushRing(entry) {
    ring.push(entry);
    ringBytes += entry.buf.byteLength;
    bySeqBytes.set(entry.seq, entry.buf.byteLength);
    while (ringBytes > RING_MAX_BYTES && ring.length) {
      const dropped = ring.shift();
      ringBytes -= dropped.buf.byteLength;
      bySeqBytes.delete(dropped.seq);
      ringBroken = true;
    }
  }

  function sendRaw(buf) {
    if (ws?.readyState === 1) {
      ws.send(buf);
      sentWindow.push({ at: Date.now(), bytes: buf.byteLength });
    }
  }

  function sendFrame(type, payload) {
    const entry = frame(type, payload);
    if (type === FRAME_INIT) {
      // A new init segment obsoletes everything before it.
      ring = [];
      ringBytes = 0;
      ringBroken = false;
      bySeqBytes.clear();
    }
    pushRing(entry);
    sendRaw(entry.buf);
  }

  function connect() {
    if (closed) return;
    if (ws && (ws.readyState === 0 || ws.readyState === 1)) return; // already connecting/open
    emit("onConnectionState", attempts === 0 ? "connecting" : "reconnecting");
    const sock = new WebSocket(url);
    ws = sock;
    ws.binaryType = "arraybuffer";
    clearTimeout(connectTimer);
    connectTimer = setTimeout(() => {
      if (ws && ws.readyState === 0) {
        try {
          ws.close();
        } catch {}
      }
    }, CONNECT_TIMEOUT_MS);

    // Events from a socket we've already replaced (e.g. after an 'online'
    // event) must not drive the current connection.
    ws.onopen = () => {
      if (sock !== ws) return;
      const hello = {
        type: "hello",
        streamKey,
        container,
        video: { codec: "avc1.42E01F", width: 1280, height: 720, fps: 30 },
        audio: { codec: "opus", sampleRate: 48000, channels: 1 },
      };
      if (sessionId && !ringBroken) hello.resume = { sessionId, lastSentSeq: seq - 1 };
      ws.send(JSON.stringify(hello));
    };

    ws.onmessage = (ev) => {
      if (sock !== ws || typeof ev.data !== "string") return;
      let msg;
      try {
        msg = JSON.parse(ev.data);
      } catch {
        return;
      }
      switch (msg.type) {
        case "ready": {
          attempts = 0;
          sessionId = msg.sessionId;
          emit("onConnectionState", "open");
          if (msg.resumed) {
            // Same ffmpeg still running server-side: retransmit un-acked.
            for (const entry of ring) {
              if (entry.seq > msg.lastSeq) sendRaw(entry.buf);
            }
            emit("onResumed");
          } else {
            // Fresh session: engine must send a new init segment.
            ring = [];
            ringBytes = 0;
            ringBroken = false;
            bySeqBytes.clear();
            emit("onReady");
          }
          lastPongAt = Date.now();
          if (!pingTimer) {
            pingTimer = setInterval(() => {
              if (ws?.readyState !== 1) return;
              // Half-open socket (silent network death): kill it so the
              // reconnect loop takes over instead of waiting on TCP timeouts.
              if (Date.now() - lastPongAt > PONG_DEAD_MS) {
                try {
                  ws.close();
                } catch {}
                return;
              }
              ws.send(JSON.stringify({ type: "ping", t: Date.now() }));
            }, PING_INTERVAL_MS);
          }
          break;
        }
        case "ack": {
          const bytes = bySeqBytes.get(msg.seq);
          if (bytes !== undefined) ackWindow.push({ at: Date.now(), bytes });
          // Acks arrive in order; drop everything up to and including seq.
          while (ring.length && ring[0].seq <= msg.seq) {
            const e = ring.shift();
            ringBytes -= e.buf.byteLength;
            bySeqBytes.delete(e.seq);
          }
          break;
        }
        case "pong":
          rtt = Date.now() - msg.t;
          lastPongAt = Date.now();
          break;
        case "stopped":
          emit("onStopped");
          break;
        case "restart-needed":
          emit("onRestartNeeded");
          break;
        case "error":
        case "fatal":
          if (msg.code === "invalid_key") {
            // Genuinely terminal — the link/key is wrong.
            emit("onFatal", msg.code, msg.message);
            closed = true;
          } else if (msg.type === "fatal") {
            emit("onFatal", msg.code, msg.message);
            sessionId = null;
          } else if (msg.code === "idle_timeout") {
            // We connected but never published (e.g. paused when the network
            // blipped). Stay down until the engine wants to publish again.
            sessionId = null;
            suspended = true;
            emit("onIdleTimeout");
          } else if (msg.code !== "rate_limited") {
            // Transient pre-publish errors (e.g. "hello timeout" on a
            // half-dead socket) — keep the reconnect loop alive. A rate
            // limit keeps the sessionId so the retry can still resume.
            sessionId = null;
          }
          break;
        default:
          break;
      }
    };

    ws.onclose = () => {
      if (sock !== ws) return;
      if (pingTimer) {
        clearInterval(pingTimer);
        pingTimer = null;
      }
      if (closed || suspended) return;
      emit("onConnectionState", "reconnecting");
      const delay = BACKOFF_MS[Math.min(attempts, BACKOFF_MS.length - 1)];
      attempts += 1;
      reconnectTimer = setTimeout(connect, delay);
    };

    ws.onerror = () => {
      // onclose always follows; nothing to do here.
    };
  }

  const prune = (win, now) => {
    while (win.length && now - win[0].at > 3000) win.shift();
  };

  return {
    connect() {
      suspended = false;
      connect();
    },
    get suspended() {
      return suspended;
    },
    sendInit: (bytes) => sendFrame(FRAME_INIT, bytes),
    sendSegment: (bytes) => sendFrame(FRAME_MEDIA, bytes),
    stop() {
      if (ws?.readyState === 1) ws.send(JSON.stringify({ type: "stop" }));
    },
    close() {
      closed = true;
      clearTimeout(reconnectTimer);
      clearTimeout(connectTimer);
      if (pingTimer) clearInterval(pingTimer);
      if (typeof window !== "undefined") {
        window.removeEventListener("offline", onOffline);
        window.removeEventListener("online", onOnline);
      }
      try {
        ws?.close();
      } catch {}
    },
    getStats() {
      const now = Date.now();
      prune(ackWindow, now);
      prune(sentWindow, now);
      const sum = (w) => w.reduce((a, x) => a + x.bytes, 0);
      return {
        rtt,
        bufferedAmount: (ws?.bufferedAmount || 0) + ringBytes,
        uplinkKbps: Math.round((sum(ackWindow) * 8) / 3000),
        sentKbps: Math.round((sum(sentWindow) * 8) / 3000),
      };
    },
  };
}
